<template>
  <div class="show-data-page" @scroll.passive="onScroll">
    <div class="show-data-header">
      <div class="show-data-header__row">
        <h1 class="show-data-title">参与用户</h1>
        <button
          type="button"
          class="show-data-refresh"
          :disabled="initialLoading || refreshing"
          aria-label="刷新"
          @click="onRefresh"
        >
          <van-icon
            name="replay"
            size="30px"
            color="#5c6bc0"
            :class="{ 'show-data-refresh__spin': refreshing }"
          />
        </button>
      </div>
      <p class="show-data-sub">活动 {{ activityCode }} · 共 {{ total }} 人</p>
    </div>

    <div v-if="initialLoading" class="show-data-status">
      <van-loading type="spinner" size="24px" />
      <span>加载中...</span>
    </div>

    <div v-else-if="!list.length" class="show-data-status show-data-status--empty">
      暂无符合条件的用户
    </div>

    <div v-else class="show-data-list">
      <div
        v-for="(item, index) in list"
        :key="'sd-' + (item.uid != null ? item.uid : index)"
        class="user-card"
      >
        <div class="user-card__head">
          <span class="user-card__name">{{ displayText(item.realName) }}</span>
          <span class="user-card__time">{{ formatTime(item.activityUserCreateTime) }}</span>
        </div>
        <div class="user-card__meta">
          <span class="user-card__meta-left">
            <span class="user-card__label">手机号</span>
            <span class="user-card__phone">{{ displayText(item.phone) }}</span>
          </span>
          <span class="user-card__meta-right">
            <span class="user-card__label">身份证</span>
            <span class="user-card__id">{{ displayText(item.cardId) }}</span>
          </span>
        </div>
        <p class="user-card__company">
          <span class="user-card__label">公司名称</span>
          <span class="user-card__company-name">{{ displayText(item.companyName) }}</span>
        </p>
      </div>
    </div>

    <div v-if="list.length" class="show-data-footer">
      <van-loading v-if="loadingMore" type="spinner" size="20px" />
      <span v-else-if="finished">没有更多了</span>
      <span v-else>上拉加载更多</span>
    </div>
  </div>
</template>

<script>
import { Icon } from 'vant'
import { fetchShowDataParticipants, SHOW_DATA_DEFAULT_ACTIVITY_CODE } from '@/api/showData'

const PAGE_SIZE = 20

export default {
  name: 'ShowDataIndex',
  components: {
    [Icon.name]: Icon
  },
  data() {
    return {
      activityCode: SHOW_DATA_DEFAULT_ACTIVITY_CODE,
      list: [],
      page: 0,
      total: 0,
      initialLoading: true,
      refreshing: false,
      loadingMore: false,
      finished: false,
      scrollReachLock: false
    }
  },
  mounted() {
    const q = (this.$route && this.$route.query) || {}
    if (q.activityCode && String(q.activityCode).trim()) {
      this.activityCode = String(q.activityCode).trim()
    }
    this.reload()
  },
  methods: {
    displayText(v) {
      const s = v != null ? String(v).trim() : ''
      return s || '-'
    },
    formatTime(v) {
      if (!v) return '-'
      const s = String(v).replace('T', ' ')
      if (s.length >= 16) return s.slice(0, 16)
      if (s.length >= 10) return s.slice(0, 10)
      return s
    },
    async onRefresh() {
      if (this.initialLoading || this.refreshing) return
      this.refreshing = true
      try {
        await this.reload({ showPageLoading: false })
      } finally {
        this.refreshing = false
      }
    },
    async reload(opts = {}) {
      const showPageLoading = opts.showPageLoading !== false
      this.page = 0
      this.list = []
      this.finished = false
      this.total = 0
      this.loadingMore = false
      this.scrollReachLock = false
      if (showPageLoading) this.initialLoading = true
      try {
        await this.loadPage(true)
      } finally {
        if (showPageLoading) this.initialLoading = false
      }
    },
    async loadPage(isFirst) {
      if (isFirst) {
        if (this.loadingMore) return
      } else if (this.loadingMore || this.finished) {
        return
      }
      if (!isFirst) this.loadingMore = true
      try {
        const res = await fetchShowDataParticipants({
          activityCode: this.activityCode,
          page: this.page,
          size: PAGE_SIZE
        })
        if (!res || Number(res.status) !== 200) {
          const msg = (res && res.msg) || '加载失败'
          this.$toast && this.$toast(msg)
          if (isFirst) this.finished = true
          return
        }
        const data = res.data || {}
        const chunk = Array.isArray(data.content) ? data.content : []
        const total = parseInt(data.totalElements, 10)
        this.total = Number.isFinite(total) ? total : chunk.length
        this.list = isFirst ? chunk : this.list.concat(chunk)
        this.page += 1
        this.finished =
          chunk.length < PAGE_SIZE ||
          (Number.isFinite(total) && this.list.length >= total)
      } catch (e) {
        this.$toast && this.$toast('加载失败，请稍后重试')
        if (isFirst) this.finished = true
      } finally {
        this.loadingMore = false
      }
    },
    onScroll(e) {
      const el = e && e.target
      if (!el || this.initialLoading || this.loadingMore || this.finished || this.scrollReachLock) {
        return
      }
      const threshold = 80
      const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - threshold
      if (!nearBottom) {
        this.scrollReachLock = false
        return
      }
      // 列表未撑满一屏时不要自动连拉下一页，避免连环请求像「一直在刷新」
      if (el.scrollHeight <= el.clientHeight + threshold) {
        return
      }
      this.scrollReachLock = true
      this.loadPage(false).finally(() => {
        this.scrollReachLock = false
      })
    }
  }
}
</script>

<style scoped lang="less">
.show-data-page {
  box-sizing: border-box;
  min-height: 100vh;
  height: 100vh;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  background: linear-gradient(180deg, #f0f4ff 0%, #f7f8fa 120px);
  padding: 16px 14px 32px;
}

.show-data-header {
  margin-bottom: 14px;
}

.show-data-header__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.show-data-title {
  margin: 0;
  flex: 1;
  min-width: 0;
  font-size: 28px;
  font-weight: 700;
  color: #1a1a2e;
  line-height: 1.3;
}

.show-data-refresh {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  margin: 0;
  padding: 0;
  border: none;
  background: rgba(92, 107, 192, 0.1);
  border-radius: 50%;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.show-data-refresh:active:not(:disabled) {
  background: rgba(92, 107, 192, 0.18);
}

.show-data-refresh:disabled {
  cursor: default;
  opacity: 0.85;
}

.show-data-refresh__spin {
  animation: show-data-spin 0.8s linear infinite;
}

@keyframes show-data-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.show-data-sub {
  margin: 6px 0 0;
  font-size: 18px;
  color: #8c8c9a;
  word-break: break-all;
}

.show-data-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.user-card {
  background: #fff;
  border-radius: 8px;
  padding: 12px 14px;
  box-shadow: 0 1px 4px rgba(26, 35, 126, 0.06);
}

.user-card__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
}

.user-card__name {
  flex: 1;
  min-width: 0;
  font-size: 25px;
  font-weight: 600;
  color: #1a1a2e;
  line-height: 1.3;
  word-break: break-all;
}

.user-card__time {
  flex-shrink: 0;
  max-width: 48%;
  font-size: 20px;
  color: #969799;
  line-height: 1.35;
  text-align: right;
  white-space: nowrap;
}

.user-card__meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 5px;
  font-size: 20px;
  line-height: 1.45;
}

.user-card__meta-left {
  flex: 0 1 auto;
  min-width: 0;
  display: inline-flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px;
}

.user-card__meta-right {
  flex: 0 1 auto;
  min-width: 0;
  max-width: 58%;
  display: inline-flex;
  align-items: baseline;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 4px;
  margin-left: auto;
  text-align: right;
}

.user-card__label {
  flex-shrink: 0;
  color: #8c8c9a;
  font-size: 18px;
}

.user-card__phone,
.user-card__id {
  color: #323233;
  font-family: Consolas, Monaco, 'PingFang SC', sans-serif;
  word-break: break-all;
}

.user-card__company {
  margin: 0;
  display: flex;
  align-items: baseline;
  gap: 4px;
  font-size: 20px;
  line-height: 1.45;
  min-width: 0;
}

.user-card__company-name {
  flex: 1;
  min-width: 0;
  color: #646566;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.show-data-status {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 48px 16px;
  color: #969799;
  font-size: 19px;
}

.show-data-status--empty {
  min-height: 40vh;
}

.show-data-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 20px 0 8px;
  font-size: 18px;
  color: #969799;
}
</style>
